import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Shield, ChevronLeft, Plus, Trash2, AlertTriangle, Info,
    Loader2, CheckCircle, ChevronDown, ChevronRight, Copy,
    RotateCcw, FileText, BookOpen, Target, Lightbulb, ArrowRight,
    Users, ClipboardList, X, Sparkles
} from 'lucide-react';
import { gradingStudio } from '../services/api';

// ── PII Detection (mirrors backend _validate_code_names logic) ──────────────
const PII_SUFFIXES = [' jr', ' sr', ' iii', ' ii'];
const SAFE_FIRST_WORDS = new Set(['student', 'group', 'team', 'kid', 'table', 'blue', 'red',
    'green', 'purple', 'orange', 'yellow', 'black', 'white', 'pink', 'pair']);

function looksLikeRealName(text) {
    if (!text || !text.trim()) return false;
    const name = text.trim();
    if (PII_SUFFIXES.some(s => name.toLowerCase().includes(s))) return true;
    const words = name.split(/\s+/);
    if (words.length === 2) {
        const hasDigit = /\d/.test(name);
        const hasDash = name.includes('-');
        if (!hasDigit && !hasDash) {
            const [first, second] = words;
            if (first[0] === first[0].toUpperCase() && second[0] === second[0].toUpperCase()) {
                if (!SAFE_FIRST_WORDS.has(first.toLowerCase())) return true;
            }
        }
    }
    return false;
}

// ── Bulk Paste Parser ────────────────────────────────────────────────────────
// Supported format:
//   Student A:
//   [work]
//
//   Student B:
//   [work]
function parseBulkPaste(raw) {
    const blocks = raw.split(/\n{2,}/);
    const rows = [];
    for (const block of blocks) {
        const trimmed = block.trim();
        if (!trimmed) continue;
        const firstLine = trimmed.split('\n')[0];
        const codenameMatch = firstLine.match(/^(.+?):\s*$/);
        if (codenameMatch) {
            const code_name = codenameMatch[1].trim();
            const work = trimmed.slice(firstLine.length).trim();
            if (code_name && work) rows.push({ code_name, work });
        }
    }
    return rows;
}

// ── Score bar color ──────────────────────────────────────────────────────────
function scoreColor(score, max) {
    const pct = max > 0 ? (score / max) * 100 : 0;
    if (pct >= 80) return { bar: '#22c55e', text: '#166534', bg: '#f0fdf4', border: '#bbf7d0' };
    if (pct >= 60) return { bar: '#f59e0b', text: '#92400e', bg: '#fffbeb', border: '#fde68a' };
    return { bar: '#ef4444', text: '#991b1b', bg: '#fef2f2', border: '#fecaca' };
}

// ── Copy all results text ────────────────────────────────────────────────────
function buildCopyText(gradingData, summary, formState) {
    const lines = [];
    lines.push('GRADING RESULTS — Saige Grading Studio');
    lines.push('='.repeat(50));
    if (summary?.total_submissions) lines.push(`Students graded: ${summary.total_submissions}`);
    if (summary?.average_score != null) lines.push(`Class average: ${summary.average_score.toFixed(1)}/${formState.maxPoints}`);
    lines.push('');

    lines.push('INDIVIDUAL FEEDBACK');
    lines.push('-'.repeat(40));
    for (const fb of (gradingData?.student_feedback || [])) {
        lines.push(`[${fb.code_name}] ${fb.score}/${fb.max_score}`);
        lines.push(`  → ${fb.feedback}`);
        if (fb.key_misconception) lines.push(`  ⚠ ${fb.key_misconception}`);
        lines.push('');
    }

    if (gradingData?.reteach_priorities?.length) {
        lines.push('RETEACH PRIORITIES');
        lines.push('-'.repeat(40));
        gradingData.reteach_priorities.forEach((p, i) => lines.push(`  ${i + 1}. ${p}`));
        lines.push('');
    }

    if (gradingData?.misconception_clusters?.length) {
        lines.push('MISCONCEPTION PATTERNS');
        lines.push('-'.repeat(40));
        for (const c of gradingData.misconception_clusters) {
            lines.push(`  • ${c.pattern} (${c.count} students: ${(c.students || []).join(', ')})`);
        }
    }
    return lines.join('\n');
}

// ── Codename Suggestions Popover ─────────────────────────────────────────────
const CODENAME_EXAMPLES = [
    'Student A, Student B, Student C…',
    'Blue-1, Blue-2, Red-1, Red-2…',
    'Table 1, Table 2, Table 3…',
    'Falcon-1, Eagle-2, Hawk-3…',
    'Group A, Group B, Group C…',
];

function CodenamePopover({ onClose }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute z-50 top-8 left-0 w-72 bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-black/5 p-4"
        >
            <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-[#1d1d1f]">Codename ideas</span>
                <button onClick={onClose} className="text-[#bbb] hover:text-[#666]"><X size={14} /></button>
            </div>
            <div className="space-y-1.5">
                {CODENAME_EXAMPLES.map((ex, i) => (
                    <div key={i} className="text-xs text-[#666] bg-[#fafafa] px-3 py-1.5 rounded-lg font-mono">{ex}</div>
                ))}
            </div>
            <p className="text-[10px] text-[#999] mt-3 leading-relaxed">
                Use any consistent system. Numbers, letters, colors, animals — anything that maps privately back to your gradebook.
            </p>
        </motion.div>
    );
}

// ── Student Row ──────────────────────────────────────────────────────────────
function StudentRow({ row, index, onChange, onRemove, showRemove }) {
    const piiWarning = looksLikeRealName(row.code_name);
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-xl border border-black/5 bg-[#fafafa] p-3 space-y-2"
        >
            <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                    {index + 1}
                </div>
                <input
                    value={row.code_name}
                    onChange={e => onChange(index, 'code_name', e.target.value)}
                    placeholder='Codename (e.g. "Student A" or "Blue-3")'
                    className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-black/10 focus:outline-none focus:border-emerald-400 bg-white"
                />
                {showRemove && (
                    <button onClick={() => onRemove(index)} className="text-[#bbb] hover:text-red-400 transition-colors p-1">
                        <Trash2 size={14} />
                    </button>
                )}
            </div>
            {piiWarning && (
                <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertTriangle size={12} className="text-amber-500 flex-shrink-0" />
                    <p className="text-[11px] text-amber-700">Looks like a real name — use a codename to keep this FERPA-safe.</p>
                </div>
            )}
            <textarea
                value={row.work}
                onChange={e => onChange(index, 'work', e.target.value)}
                placeholder="Paste the student's response or work here…"
                rows={3}
                className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 focus:outline-none focus:border-emerald-400 resize-none bg-white"
            />
        </motion.div>
    );
}

// ── PII Confirm Modal ────────────────────────────────────────────────────────
function PiiConfirmModal({ names, onConfirm, onBack }) {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        >
            <motion.div
                initial={{ scale: 0.92, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.92, opacity: 0 }}
                className="bg-white rounded-2xl p-6 shadow-[0_12px_40px_rgba(0,0,0,0.12)] max-w-sm w-full"
            >
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle size={20} className="text-amber-600" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-[#1d1d1f]">Possible real names detected</h3>
                        <p className="text-xs text-[#999]">Review before submitting</p>
                    </div>
                </div>
                <p className="text-sm text-[#444] mb-3 leading-relaxed">
                    These codenames look like they could be real student names:
                </p>
                <div className="space-y-1 mb-4">
                    {names.map((n, i) => (
                        <div key={i} className="text-xs font-mono bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5 text-amber-800">
                            "{n}"
                        </div>
                    ))}
                </div>
                <p className="text-xs text-[#666] mb-5 leading-relaxed">
                    For FERPA safety, use codenames like "Student A" or "Blue-3" instead of real names. You can go back and update them, or continue if you're confident these are safe.
                </p>
                <div className="flex gap-2">
                    <button
                        onClick={onBack}
                        className="flex-1 py-2 text-sm font-medium text-[#444] bg-[#f5f5f7] hover:bg-slate-100 rounded-xl transition-colors"
                    >
                        ← Go back
                    </button>
                    <button
                        onClick={onConfirm}
                        className="flex-1 py-2 text-sm font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-xl transition-colors"
                    >
                        Submit anyway
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}

// ── In-Progress View ─────────────────────────────────────────────────────────
const PROGRESS_MESSAGES = [
    'Reading student responses…',
    'Applying your rubric…',
    'Checking standards alignment…',
    'Identifying misconceptions…',
    'Building feedback…',
    'Almost done…',
];

function GradingProgressView({ formState }) {
    const [msgIndex, setMsgIndex] = React.useState(0);
    React.useEffect(() => {
        const interval = setInterval(() => setMsgIndex(i => (i + 1) % PROGRESS_MESSAGES.length), 2200);
        return () => clearInterval(interval);
    }, []);

    const contextItems = [
        { label: 'Student work', active: true },
        { label: 'Rubric', active: !!formState.rubric },
        { label: 'Standards', active: Array.isArray(formState.standards) ? formState.standards.length > 0 : !!(formState.standards && String(formState.standards).trim().length > 0) },
        { label: 'Assignment instructions', active: !!formState.instructions },
    ];

    return (
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-6 shadow-[0_8px_20px_rgba(16,185,129,0.3)]">
                <Shield size={28} className="text-white" />
            </div>
            <div className="w-48 h-1.5 bg-[#f5f5f7] rounded-full mb-6 overflow-hidden">
                <motion.div
                    className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full"
                    initial={{ width: '5%' }}
                    animate={{ width: '90%' }}
                    transition={{ duration: 12, ease: 'easeInOut' }}
                />
            </div>
            <AnimatePresence mode="wait">
                <motion.p
                    key={msgIndex}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    className="text-sm font-medium text-[#1d1d1f] mb-1"
                >
                    {PROGRESS_MESSAGES[msgIndex]}
                </motion.p>
            </AnimatePresence>
            <p className="text-xs text-[#999] mb-10">Saige is grading privately — no student names leave your session</p>
            <div className="bg-[#fafafa] rounded-2xl p-4 border border-black/5 w-full max-w-xs text-left">
                <p className="text-[11px] font-semibold text-[#666] uppercase tracking-wide mb-3">Saige is using</p>
                <div className="space-y-2">
                    {contextItems.map(({ label, active }) => (
                        <div key={label} className="flex items-center gap-2.5">
                            <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${active ? 'bg-emerald-100' : 'bg-[#eee]'}`}>
                                {active
                                    ? <CheckCircle size={11} className="text-emerald-600" />
                                    : <FileText size={11} className="text-[#bbb]" />}
                            </div>
                            <span className={`text-xs ${active ? 'text-[#1d1d1f] font-medium' : 'text-[#bbb]'}`}>{label}</span>
                            {!active && <span className="text-[10px] text-[#bbb]">(not provided)</span>}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ── Student Result Card ───────────────────────────────────────────────────────
function StudentResultCard({ fb }) {
    const [expanded, setExpanded] = useState(false);
    const colors = scoreColor(fb.score, fb.max_score);
    const pct = fb.max_score > 0 ? Math.round((fb.score / fb.max_score) * 100) : 0;
    const hasStandards = fb.standard_alignment && Object.keys(fb.standard_alignment).length > 0;
    const statusLabel = { met: 'Met', partial: 'Partial', not_met: 'Not Met' };
    const statusColor = {
        met: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        partial: 'bg-amber-100 text-amber-700 border-amber-200',
        not_met: 'bg-red-100 text-red-700 border-red-200',
    };

    return (
        <div className="rounded-xl border border-black/5 overflow-hidden bg-white">
            <div
                className="p-4 cursor-pointer hover:bg-[#fafafa] transition-colors"
                onClick={() => setExpanded(e => !e)}
            >
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 text-xs font-bold"
                        style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
                        {pct}%
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold text-[#1d1d1f] truncate">{fb.code_name}</span>
                            <span className="text-xs font-mono text-[#666] ml-2 flex-shrink-0">{fb.score}/{fb.max_score}</span>
                        </div>
                        <div className="w-full h-1.5 bg-[#f0f0f0] rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: colors.bar }} />
                        </div>
                    </div>
                    <ChevronDown size={14} className={`text-[#bbb] flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                </div>
            </div>
            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-black/5"
                    >
                        <div className="p-4 space-y-3">
                            <p className="text-sm text-[#444] leading-relaxed">{fb.feedback}</p>
                            {fb.key_misconception && (
                                <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-100 rounded-lg">
                                    <AlertTriangle size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-amber-700">{fb.key_misconception}</p>
                                </div>
                            )}
                            {hasStandards && (
                                <div className="flex flex-wrap gap-1.5">
                                    {Object.entries(fb.standard_alignment).map(([std, status]) => (
                                        <span key={std} className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${statusColor[status] || 'bg-[#f5f5f7] text-[#666] border-[#eee]'}`}>
                                            {std}: {statusLabel[status] || status}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// ── Results View ─────────────────────────────────────────────────────────────
function ResultsView({ gradingData, summary, formState, onStartOver, onGradeMore, onCopy, copied }) {
    const [showMisconceptions, setShowMisconceptions] = useState(false);
    const avgScore = summary?.average_score;
    const total = summary?.total_submissions || 0;
    const colors = avgScore != null ? scoreColor(avgScore, formState.maxPoints) : null;

    const contextItems = [
        { label: 'Student work', active: true },
        { label: 'Rubric', active: !!formState.rubric },
        { label: 'Standards', active: Array.isArray(formState.standards) ? formState.standards.length > 0 : !!(formState.standards && String(formState.standards).trim().length > 0) },
        { label: 'Instructions', active: !!formState.instructions },
    ].filter(i => i.active).map(i => i.label);

    return (
        <div className="space-y-5">
            {/* Summary header */}
            <div className="flex items-center justify-between p-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-[0_4px_12px_rgba(16,185,129,0.3)]">
                        <CheckCircle size={20} className="text-white" />
                    </div>
                    <div>
                        <p className="text-sm font-bold text-emerald-800">Grading complete</p>
                        <p className="text-xs text-emerald-600">{total} student{total !== 1 ? 's' : ''} reviewed</p>
                    </div>
                </div>
                {avgScore != null && (
                    <div className="text-right">
                        <p className="text-lg font-bold" style={{ color: colors?.text }}>{avgScore.toFixed(1)}</p>
                        <p className="text-[11px] text-[#999]">class avg / {formState.maxPoints}</p>
                    </div>
                )}
            </div>

            {/* What Saige used */}
            <div className="flex items-center gap-2 px-3 py-2 bg-[#fafafa] rounded-xl border border-black/5">
                <Info size={13} className="text-[#999] flex-shrink-0" />
                <p className="text-xs text-[#666]">Saige evaluated using: <span className="font-medium text-[#444]">{contextItems.join(' · ')}</span></p>
            </div>

            {/* Student cards */}
            <div>
                <h3 className="text-xs font-semibold text-[#666] uppercase tracking-wide mb-3">Student Results</h3>
                <div className="space-y-2">
                    {(gradingData?.student_feedback || []).map(fb => (
                        <StudentResultCard key={fb.code_name} fb={fb} />
                    ))}
                </div>
            </div>

            {/* Reteach priorities */}
            {gradingData?.reteach_priorities?.length > 0 && (
                <div className="p-4 rounded-xl bg-rose-50 border border-rose-100">
                    <div className="flex items-center gap-2 mb-3">
                        <Target size={15} className="text-rose-600" />
                        <h3 className="text-xs font-bold text-rose-800 uppercase tracking-wide">Reteach Priorities</h3>
                    </div>
                    <ol className="space-y-1.5">
                        {gradingData.reteach_priorities.map((p, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-rose-700">
                                <span className="font-bold flex-shrink-0">{i + 1}.</span>
                                <span className="leading-relaxed">{p}</span>
                            </li>
                        ))}
                    </ol>
                </div>
            )}

            {/* Misconception clusters */}
            {gradingData?.misconception_clusters?.length > 0 && (
                <div className="rounded-xl border border-black/5 overflow-hidden">
                    <button
                        onClick={() => setShowMisconceptions(e => !e)}
                        className="w-full p-4 flex items-center justify-between hover:bg-[#fafafa] transition-colors"
                    >
                        <div className="flex items-center gap-2">
                            <Lightbulb size={15} className="text-amber-500" />
                            <span className="text-sm font-semibold text-[#1d1d1f]">Misconception Patterns</span>
                            <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                                {gradingData.misconception_clusters.length}
                            </span>
                        </div>
                        <ChevronDown size={14} className={`text-[#bbb] transition-transform ${showMisconceptions ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                        {showMisconceptions && (
                            <motion.div
                                initial={{ height: 0 }}
                                animate={{ height: 'auto' }}
                                exit={{ height: 0 }}
                                className="overflow-hidden border-t border-black/5"
                            >
                                <div className="p-4 space-y-3">
                                    {gradingData.misconception_clusters.map((c, i) => (
                                        <div key={i} className="p-3 bg-amber-50 rounded-xl border border-amber-100">
                                            <p className="text-sm font-semibold text-amber-800 mb-1">{c.pattern}</p>
                                            <p className="text-xs text-amber-600">{c.count} student{c.count !== 1 ? 's' : ''}: {(c.students || []).join(', ')}</p>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-2 pt-2">
                <button
                    onClick={onCopy}
                    className="w-full py-3 flex items-center justify-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all shadow-[0_4px_12px_rgba(16,185,129,0.3)] active:scale-[0.98]"
                >
                    {copied ? <><CheckCircle size={16} /> Copied!</> : <><Copy size={16} /> Copy All Results</>}
                </button>
                <div className="grid grid-cols-2 gap-2">
                    <button
                        onClick={onGradeMore}
                        className="py-2.5 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-colors flex items-center justify-center gap-1.5"
                    >
                        <Plus size={14} /> Grade More
                    </button>
                    <button
                        onClick={onStartOver}
                        className="py-2.5 text-sm font-medium text-[#666] bg-[#fafafa] border border-black/5 rounded-xl hover:bg-slate-100 transition-colors flex items-center justify-center gap-1.5"
                    >
                        <RotateCcw size={14} /> Start Over
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Setup Form ────────────────────────────────────────────────────────────────
const DEFAULT_ROW = () => ({ code_name: '', work: '' });

function SetupView({ onSubmit, isSubmitting }) {
    const [rows, setRows] = useState([DEFAULT_ROW(), DEFAULT_ROW()]);
    const [showOptional, setShowOptional] = useState(false);
    const [showBulkPaste, setShowBulkPaste] = useState(false);
    const [bulkText, setBulkText] = useState('');
    const [bulkError, setBulkError] = useState('');
    const [showCodenamePopover, setShowCodenamePopover] = useState(false);
    const [formState, setFormState] = useState({ instructions: '', rubric: '', standards: '', maxPoints: 100 });
    const [formError, setFormError] = useState('');

    const piiNames = rows.filter(r => looksLikeRealName(r.code_name)).map(r => r.code_name);

    const updateRow = (idx, field, value) => {
        setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    };
    const addRow = () => setRows(prev => [...prev, DEFAULT_ROW()]);
    const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));

    const applyBulkPaste = () => {
        const parsed = parseBulkPaste(bulkText);
        if (parsed.length === 0) {
            setBulkError('No valid entries found. Format: "Codename:\\n[work]" separated by blank lines.');
            return;
        }
        setRows(parsed);
        setShowBulkPaste(false);
        setBulkText('');
        setBulkError('');
    };

    const handleSubmit = () => {
        setFormError('');
        const valid = rows.filter(r => r.code_name.trim() && r.work.trim());
        if (valid.length === 0) {
            setFormError('Add at least one student with a codename and response.');
            return;
        }
        const standards = formState.standards.split(',').map(s => s.trim()).filter(Boolean);
        onSubmit({ rows: valid, formState: { ...formState, standards } }, piiNames);
    };

    return (
        <div className="space-y-6">
            {/* FERPA banner */}
            <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl">
                <Shield size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                    <p className="text-sm font-semibold text-emerald-800 mb-0.5">FERPA-safe grading</p>
                    <p className="text-xs text-emerald-700 leading-relaxed">
                        Use student codenames instead of real names. Saige validates each submission and will warn you if a real name is detected.
                    </p>
                </div>
            </div>

            {/* Step explainer */}
            <div className="grid grid-cols-4 gap-2">
                {[
                    { n: '1', label: 'Add students', sub: 'codenames only' },
                    { n: '2', label: 'Paste work', sub: 'responses or essays' },
                    { n: '3', label: 'Set context', sub: 'rubric, standards' },
                    { n: '4', label: 'Review results', sub: 'scores & feedback' },
                ].map(step => (
                    <div key={step.n} className="text-center p-2 rounded-xl bg-[#fafafa] border border-black/5">
                        <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center mx-auto mb-1">{step.n}</div>
                        <p className="text-[10px] font-semibold text-[#1d1d1f]">{step.label}</p>
                        <p className="text-[9px] text-[#999]">{step.sub}</p>
                    </div>
                ))}
            </div>

            {/* Student submissions section */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Users size={15} className="text-[#444]" />
                        <span className="text-sm font-semibold text-[#1d1d1f]">Student submissions</span>
                        <span className="text-[10px] text-white bg-rose-500 px-1.5 py-0.5 rounded font-bold">required</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <button
                                onClick={() => setShowCodenamePopover(v => !v)}
                                className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1 transition-colors"
                            >
                                <Sparkles size={12} /> Codename ideas
                            </button>
                            <AnimatePresence>
                                {showCodenamePopover && <CodenamePopover onClose={() => setShowCodenamePopover(false)} />}
                            </AnimatePresence>
                        </div>
                        <span className="text-[#ddd]">|</span>
                        <button
                            onClick={() => setShowBulkPaste(v => !v)}
                            className="text-xs text-[#666] hover:text-[#1d1d1f] flex items-center gap-1 transition-colors"
                        >
                            <FileText size={12} /> Bulk paste
                        </button>
                    </div>
                </div>

                {/* Bulk paste helper */}
                <AnimatePresence>
                    {showBulkPaste && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="mb-3 overflow-hidden"
                        >
                            <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl space-y-3">
                                <p className="text-xs font-semibold text-blue-800">Bulk paste format</p>
                                <p className="text-[11px] text-blue-600 font-mono bg-blue-100 rounded-lg p-2 leading-relaxed">
                                    Student A:<br />
                                    [response text]<br /><br />
                                    Student B:<br />
                                    [response text]
                                </p>
                                <textarea
                                    value={bulkText}
                                    onChange={e => { setBulkText(e.target.value); setBulkError(''); }}
                                    placeholder="Paste all student work here…"
                                    rows={5}
                                    className="w-full px-3 py-2 text-sm rounded-lg border border-blue-200 focus:outline-none focus:border-blue-400 resize-none bg-white"
                                />
                                {bulkError && <p className="text-xs text-red-500">{bulkError}</p>}
                                <div className="flex gap-2">
                                    <button onClick={applyBulkPaste} className="px-4 py-2 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                                        Parse & Fill Rows
                                    </button>
                                    <button onClick={() => { setShowBulkPaste(false); setBulkError(''); setBulkText(''); }} className="px-4 py-2 text-xs font-medium text-[#666] bg-[#f5f5f7] rounded-lg transition-colors">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="space-y-3">
                    <AnimatePresence>
                        {rows.map((row, idx) => (
                            <StudentRow
                                key={idx}
                                row={row}
                                index={idx}
                                onChange={updateRow}
                                onRemove={removeRow}
                                showRemove={rows.length > 1}
                            />
                        ))}
                    </AnimatePresence>
                </div>
                <button
                    onClick={addRow}
                    className="mt-3 w-full py-2.5 text-sm font-medium text-emerald-700 border border-dashed border-emerald-300 rounded-xl hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2"
                >
                    <Plus size={15} /> Add another student
                </button>
            </div>

            {/* Optional context section */}
            <div>
                <button
                    onClick={() => setShowOptional(v => !v)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-[#fafafa] border border-black/5 rounded-xl hover:bg-slate-50 transition-colors"
                >
                    <div className="flex items-center gap-2">
                        <ClipboardList size={15} className="text-[#666]" />
                        <span className="text-sm font-semibold text-[#1d1d1f]">Grading context</span>
                        <span className="text-[10px] text-[#999] bg-[#eee] px-1.5 py-0.5 rounded font-medium">optional</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-[#999]">rubric / standards / instructions</span>
                        {showOptional ? <ChevronDown size={14} className="text-[#bbb]" /> : <ChevronRight size={14} className="text-[#bbb]" />}
                    </div>
                </button>

                <AnimatePresence>
                    {showOptional && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="pt-3 space-y-3">
                                <div>
                                    <label className="text-xs font-semibold text-[#666] mb-1.5 block">Assignment instructions</label>
                                    <textarea
                                        value={formState.instructions}
                                        onChange={e => setFormState(p => ({ ...p, instructions: e.target.value }))}
                                        placeholder="What were students asked to do? (helps Saige grade more accurately)"
                                        rows={2}
                                        className="w-full px-3 py-2 text-sm rounded-xl border border-black/10 focus:outline-none focus:border-emerald-400 resize-none bg-[#fafafa]"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-[#666] mb-1.5 block">Rubric criteria</label>
                                    <textarea
                                        value={formState.rubric}
                                        onChange={e => setFormState(p => ({ ...p, rubric: e.target.value }))}
                                        placeholder="Paste or describe your rubric here (e.g. 4=Excellent, 3=Proficient, 2=Approaching, 1=Below)"
                                        rows={3}
                                        className="w-full px-3 py-2 text-sm rounded-xl border border-black/10 focus:outline-none focus:border-emerald-400 resize-none bg-[#fafafa]"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-semibold text-[#666] mb-1.5 block">Standards (comma-separated)</label>
                                        <input
                                            value={formState.standards}
                                            onChange={e => setFormState(p => ({ ...p, standards: e.target.value }))}
                                            placeholder="e.g. TEKS 5.3A, TEKS 5.3B"
                                            className="w-full px-3 py-2 text-sm rounded-xl border border-black/10 focus:outline-none focus:border-emerald-400 bg-[#fafafa]"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-[#666] mb-1.5 block">Points possible</label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={1000}
                                            value={formState.maxPoints}
                                            onChange={e => setFormState(p => ({ ...p, maxPoints: Math.max(1, parseInt(e.target.value) || 100) }))}
                                            className="w-full px-3 py-2 text-sm rounded-xl border border-black/10 focus:outline-none focus:border-emerald-400 bg-[#fafafa]"
                                        />
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Error */}
            {formError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                    <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
                    <p className="text-xs text-red-600">{formError}</p>
                </div>
            )}

            {/* Submit */}
            <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                id="grading-studio-submit"
                className="w-full py-3.5 flex items-center justify-center gap-2 text-base font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl hover:from-emerald-600 hover:to-teal-700 disabled:opacity-60 transition-all shadow-[0_4px_16px_rgba(16,185,129,0.3)] active:scale-[0.99]"
            >
                {isSubmitting
                    ? <><Loader2 size={18} className="animate-spin" /> Grading…</>
                    : <><span>Grade {rows.filter(r => r.code_name.trim() && r.work.trim()).length || rows.length} Student{rows.length !== 1 ? 's' : ''}</span> <ArrowRight size={18} /></>
                }
            </button>
        </div>
    );
}

// ── Main GradingStudio Component ─────────────────────────────────────────────
export default function GradingStudio({ userProfile, onClose }) {
    const [view, setView] = useState('setup'); // 'setup' | 'grading' | 'results'
    const [gradingData, setGradingData] = useState(null);
    const [summary, setSummary] = useState(null);
    const [formStateRef, setFormStateRef] = useState(null);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [piiModalState, setPiiModalState] = useState(null); // { names, payload }

    const doGradeRef = React.useRef(null);

    const doGrade = useCallback(async (payload) => {
        setPiiModalState(null);
        setError('');
        setFormStateRef(payload.formState);
        setView('grading');

        try {
            const res = await gradingStudio.evaluate({
                teacher_id: userProfile.email,
                submissions: payload.rows,
                rubric_text: payload.formState.rubric || undefined,
                standards: payload.formState.standards || [],
                max_points: payload.formState.maxPoints,
            });

            if (res.data?.success === false) {
                setError(res.data?.error || 'Grading failed. Please try again.');
                setView('setup');
                return;
            }

            setGradingData(res.data?.data || null);
            setSummary(res.data?.summary || null);
            setView('results');
        } catch (err) {
            const detail = err.response?.data?.detail || err.message || 'Grading failed.';
            if (detail.includes('FERPA') || detail.includes('real name')) {
                setError('FERPA check failed: ' + detail + ' Please use codenames only.');
            } else if (err.response?.status === 429 || detail.includes('limit') || detail.includes('quota')) {
                setError('You\'ve reached your usage limit for this period. Grading shares your lesson generation quota.');
            } else {
                setError('Something went wrong: ' + detail);
            }
            setView('setup');
        }
    }, [userProfile.email]);

    React.useEffect(() => { doGradeRef.current = doGrade; }, [doGrade]);

    const handleSetupSubmit = useCallback((payload, piiNames) => {
        if (piiNames.length > 0) {
            setPiiModalState({ names: piiNames, payload });
        } else {
            doGradeRef.current?.(payload);
        }
    }, []);

    const handleCopy = () => {
        const text = buildCopyText(gradingData, summary, formStateRef);
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleStartOver = () => {
        setView('setup');
        setGradingData(null);
        setSummary(null);
        setError('');
    };

    const handleGradeMore = () => {
        // Keep results visible, reopen same session for additional students
        setView('setup');
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-md border-b border-[#f5f5f7] sticky top-0 z-40">
                <div className="max-w-[700px] mx-auto px-6 h-14 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="p-1.5 text-[#999] hover:text-[#444] transition-colors"
                            title="Back to dashboard"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                            <Shield size={14} className="text-white" />
                        </div>
                        <span className="font-semibold text-[#1d1d1f] text-sm">Grading Studio</span>
                        <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full text-[10px] font-semibold text-emerald-700">
                            <Shield size={9} /> FERPA-Safe
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {view === 'results' && (
                            <span className="text-xs text-[#999]">
                                {summary?.total_submissions || 0} students · avg {summary?.average_score?.toFixed(1) || '—'}/{formStateRef?.maxPoints || 100}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="max-w-[700px] mx-auto px-6 py-8">
                {/* Global error */}
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl"
                    >
                        <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-semibold text-red-700 mb-0.5">Grading error</p>
                            <p className="text-xs text-red-600 leading-relaxed">{error}</p>
                        </div>
                        <button onClick={() => setError('')} className="ml-auto text-[#bbb] hover:text-red-400">
                            <X size={14} />
                        </button>
                    </motion.div>
                )}

                <AnimatePresence mode="wait">
                    {view === 'setup' && (
                        <motion.div key="setup" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                            <SetupView onSubmit={handleSetupSubmit} isSubmitting={false} />
                        </motion.div>
                    )}
                    {view === 'grading' && (
                        <motion.div key="grading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                            <GradingProgressView formState={formStateRef || {}} />
                        </motion.div>
                    )}
                    {view === 'results' && (
                        <motion.div key="results" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                            <ResultsView
                                gradingData={gradingData}
                                summary={summary}
                                formState={formStateRef || { maxPoints: 100 }}
                                onStartOver={handleStartOver}
                                onGradeMore={handleGradeMore}
                                onCopy={handleCopy}
                                copied={copied}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* PII confirm modal */}
            <AnimatePresence>
                {piiModalState && (
                    <PiiConfirmModal
                        names={piiModalState.names}
                        onConfirm={() => doGrade(piiModalState.payload)}
                        onBack={() => setPiiModalState(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}
